// src/steps/Step5Suggestions.js
import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";

import FrameworkHeader from "../components/FrameworkHeader";
import FrameworkSuggestionViewer from "../components/FrameworkSuggestionViewer";

export default function Step5Suggestions({
  futureSelection,
  shortlistPreview,
  loading,
  error,
  onRetry,
  certificationNeeded = false,
}) {
  // Light tint background colors
  const frameworkColors = {
    EDU: "#ffe6e6",
    GOV: "#e6f4e6",
    TEC: "#e6ebff",
    EID: "#fff7e6",
  };

  // Group shortlist items by prefix
  const groups = {
    EDU: [],
    GOV: [],
    TEC: [],
    EID: [],
  };

  (shortlistPreview || []).forEach((item) => {
    const id = item.id.toUpperCase();
    if (id.startsWith("EDU")) groups.EDU.push(item);
    else if (id.startsWith("GOV")) groups.GOV.push(item);
    else if (id.startsWith("TEC")) groups.TEC.push(item);
    else if (id.startsWith("EID")) groups.EID.push(item);
  });

  return (
    <Box>
      {/* Step Title */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Step 5 · Suggested MLUs for Future Learning
      </Typography>

      {/* Explanation Text */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Based on the information you provided in Steps 1–4, the{" "}
        <strong>K8 designed T4R Learning Journey Design Agent</strong> used {" "}
        <strong>Mistral's Large Language Model (LLM)</strong> to generate
        suggest microlearning units for each framework. You can review these
        pre-selected MLUs here and finalise the selection in Step 6.
      </Typography>

      {/* Loading state for suggestions */}
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading suggestions…</Typography>
        </Box>
      )}

      {/* Error state */}
      {error && !loading && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            onRetry && (
              <Button size="small" color="inherit" onClick={onRetry}>
                Retry
              </Button>
            )
          }
        >
          {error}
        </Alert>
      )}

      {/* Framework sections */}
      {!loading && !error && futureSelection && (
        <Box sx={{ mt: 3 }}>
          {/* --- Education (EDU) --- */}
          <FrameworkHeader
            title="Education (EDU)"
            tooltip="Education (EDU) MLUs focus on foundational concepts, learning pathways, 
                     and capacity building for working with Local Digital Twins."
          />
          <FrameworkSuggestionViewer
            label=""
            codePrefix="EDU"
            suggestions={futureSelection.education || []}
            shortlist={groups.EDU}
            bgColor={frameworkColors.EDU}
            imageSrc={`${process.env.PUBLIC_URL}/EDU.jpg`}
            certificationNeeded={certificationNeeded}
          />

          {/* --- Ethics, Inclusion & Democratisation (EID) --- */}
          <Box sx={{ mt: 3 }} />
          <FrameworkHeader
            title="Ethics, Inclusion & Democratisation (EID)"
            tooltip="EID MLUs address ethical use of data & AI, inclusive participation, 
                     and democratisation of Local Digital Twin practices."
          />
          <FrameworkSuggestionViewer
            label=""
            codePrefix="EID"
            suggestions={futureSelection.ethics || []}
            shortlist={groups.EID}
            bgColor={frameworkColors.EID}
            imageSrc={`${process.env.PUBLIC_URL}/EID.jpg`}
            certificationNeeded={certificationNeeded}
          />

          {/* --- Governance (GOV) --- */}
          <Box sx={{ mt: 3 }} />
          <FrameworkHeader
            title="Governance (GOV)"
            tooltip="Governance (GOV) MLUs address decision-making structures, coordination 
                     across departments, and the policy context of Local Digital Twins."
          />
          <FrameworkSuggestionViewer
            label=""
            codePrefix="GOV"
            suggestions={futureSelection.governance || []}
            shortlist={groups.GOV}
            bgColor={frameworkColors.GOV}
            imageSrc={`${process.env.PUBLIC_URL}/GOV.jpg`}
            certificationNeeded={certificationNeeded}
          />

          {/* --- Technical Design (TEC) --- */}
          <Box sx={{ mt: 3 }} />
          <FrameworkHeader
            title="Technical Design (TEC)"
            tooltip="Technical Design (TEC) MLUs focus on data architectures, modelling, 
                     integration, and infrastructure for Local Digital Twins."
          />
          <FrameworkSuggestionViewer
            label=""
            codePrefix="TEC"
            suggestions={futureSelection.technical || []}
            shortlist={groups.TEC}
            bgColor={frameworkColors.TEC}
            imageSrc={`${process.env.PUBLIC_URL}/TEC.jpg`}
            certificationNeeded={certificationNeeded}
          />
        </Box>
      )}
    </Box>
  );
}
